/*
 * IMPORTS
 */
import React, { useState, useCallback, useMemo } from "react";
import {
  Box, Flex, Text, Spinner, Button, Select,
  useStyleConfig, useColorModeValue, useBreakpointValue,
  Modal, ModalOverlay, ModalContent, ModalBody
} from "@chakra-ui/react";
import { useMutation } from "@apollo/client";
import { toast } from 'react-hot-toast'; //  react-hot-toast for consistency


/*
 * COMPONENTS
 */
import DateRangePicker from "components/DateRangePicker";
import DeleteCard from "components/DeleteCard";


/*
 * GRAPHS
 */
import DeleteDataMutation from "./__mutation__/index.delete.data.mutation";


/*
 * CONSTANTS
 */
// Delete operation types
const DELETE_TYPES = ["SOFT", "HARD", "UNDO"];


// Table selection options
const TABLE_OPTIONS = [
  { value: "Sms", label: "SMS" },
  { value: "logs", label: "LOGS" }
];


// Button configuration for each delete type
const BUTTON_CONFIG = {
  SOFT: { text: "ARCHIVE", bgColor: "blue.500", hoverColor: "blue.600" },
  HARD: { text: "DELETE", bgColor: "red.500", hoverColor: "red.600" },
  UNDO: { text: "UNDO", bgColor: "orange.500", hoverColor: "orange.600" }
};


/*
 * COMPONENT
 */
export default function DeleteDataManager({ title }) {
  // Chakra UI styles
  const _styles = useStyleConfig("Card");

  // Responsive breakpoint detection
  const _isCurrentViewMobile = useBreakpointValue(
    { base: true, sm: true, md: false, lg: false, xl: false },
    { fallback: "base" }
  );


  /*
   * STATE MANAGEMENT
   */
  // Modal visibility state
  const [isDeleteCardOpen, setIsDeleteCardOpen] = useState(false);

  const [deleteType, setDeleteType] = useState("SOFT");
  const [tableName, setTableName] = useState("Sms");

  const [dateRange, setDateRange] = useState({
    selection: {
      'startDate': Object?.DateRangeProvider?.()?.startDate,
      'endDate': Object?.DateRangeProvider?.()?.endDate,
      key: "selection",
    },
  });


  /*
   * GRAPHQL MUTATION
   */
  // Delete data mutation 
  const [MutationDeleteData, MutationDeleteDataResponse] = useMutation(DeleteDataMutation);


  /*
   * DELETE DATA HANDLER
   */

  const handleDeleteData = useCallback(() => {
    const { startDate, endDate } = dateRange.selection;

    // Execute GraphQL mutation
    MutationDeleteData({
      variables: {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        tableName,
        type: deleteType
      }
    }).then(i => {
      // Style Guide 
      toast(i?.data?.DeleteData?.message);

      // Report success - close modal
      return setIsDeleteCardOpen(false);
    }).catch(() =>
      // Style Guide - consistent error message
      toast('Something went wrong please try after some time.')
    );
  }, [dateRange, tableName, deleteType, MutationDeleteData]);


  /*
   * COMPUTED VALUES
   */
  // Get button configuration based on delete type
  const buttonConfig = BUTTON_CONFIG[deleteType];

  // Generate dynamic subheading for delete card
  const deleteCardSubHeading = useMemo(() =>
    `${deleteType === "UNDO" ? "undo" : "delete"} records from ${tableName} table`,
    [deleteType, tableName]
  );


  /*
   * RENDER
   */
  return (
    <>
      {/* MAIN CONTAINER */}
      <Box
        __css={_styles}
        borderRadius="2xl"
        p={[3, null, 8]}
        maxW="900px"
        fontSize="14px"
        bg={useColorModeValue("white", "gray.900")}
        background="linear-gradient(23deg, #FFDBE7 0%, #BCC0FF 99.80%)"
      >
        {/* HEADER SECTION  */}
        <Flex
          w="100%"
          align="center"
          justify="space-between"
          flexDir={_isCurrentViewMobile ? "column" : "row"}
          gap={4}
          mb={4}
        >

          <Flex direction="column">
            <Text
              color="rgba(43,54,116,1)"
              fontWeight="700"
              fontSize="18px"
            >
              {title}
            </Text>

            <Text
              fontSize="14px"
              color="rgba(141, 151, 182, 1)"
              fontWeight="500"
            >
              Performs archive, delete, or restore on selected SMS and log records
            </Text>
          </Flex>
          <Flex
            justify="flex-end"
            w={_isCurrentViewMobile ? "100%" : "auto"}
          >
            <DateRangePicker
              startDate={dateRange.selection.startDate}
              endDate={dateRange.selection.endDate}
              setSelectDate={setDateRange}
            />
          </Flex>

        </Flex>


        {/* CONTROLS SECTION  */}
        <Flex
          w="100%"
          gap={_isCurrentViewMobile ? 3 : 3}
          flexDirection={_isCurrentViewMobile ? "column" : "row"}
          align="center"
        >
          {/* TABLE SELECTOR */}
          <Select
            size="lg"
            flex={_isCurrentViewMobile ? 0 : 1}
            w={_isCurrentViewMobile ? "100%" : "auto"}
            fontSize="13px"
            fontWeight={600}
            height="34px"
            borderRadius="2xl"
            bg="#ffffff"
            value={tableName}
            onChange={(e) => setTableName(e.target.value)}
          >
            {TABLE_OPTIONS.map(({ value, label }) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </Select>

          {/* DELETE TYPE SELECTOR */}
          <Select
            size="lg"
            flex={_isCurrentViewMobile ? 0 : 0.8}
            w={_isCurrentViewMobile ? "100%" : "auto"}
            fontSize="13px"
            fontWeight={600}
            height="34px"
            borderRadius="2xl"
            bg="#ffffff"
            value={deleteType}
            onChange={(e) => setDeleteType(e.target.value)}
            _hover={{ bg: "#f0f0f0" }}
            sx={{
              "& option": { fontSize: "14px", color: "black" }
            }}
          >
            {DELETE_TYPES.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </Select>

          {/* DELETE ACTION BUTTON */}
          <Button
            onClick={() => setIsDeleteCardOpen(true)}
            background={buttonConfig.bgColor}
            color="white"
            size="md"
            height="34px"
            px={5}
            borderRadius="2xl"
            fontWeight={600}
            fontSize="12px"
            _hover={{ bg: buttonConfig.hoverColor, transform: "scale(1.02)" }}
            isDisabled={MutationDeleteDataResponse.loading}
            isLoading={MutationDeleteDataResponse.loading}
            loadingText="..."
            w={_isCurrentViewMobile ? "100%" : "auto"}
          >
            {buttonConfig.text}
          </Button>
        </Flex>
      </Box >


      {/* DELETE CONFIRMATION MODAL */}
      < Modal
        isOpen={isDeleteCardOpen}
        onClose={() => !MutationDeleteDataResponse.loading && setIsDeleteCardOpen(false)
        }
        isCentered
        closeOnOverlayClick={!MutationDeleteDataResponse.loading}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalBody>

            <DeleteCard
              onClose={() => setIsDeleteCardOpen(false)}
              disabled={MutationDeleteDataResponse.loading}
              onConfirm={handleDeleteData}
              isLoading={MutationDeleteDataResponse.loading}
              subHeading={deleteCardSubHeading}
            />
          </ModalBody>
        </ModalContent>
      </Modal >
    </>
  );
}
